import { useEffect, useState } from "react";
import { pfaList } from "../../actions/payments";
import { PFAListType } from "../../types";

export const scheduleHeaders = {
  employerCode: "EMPLOYER CODE",
  employeeName: "Name of Employee",
  normalContributionEmployee: "Normal contribution for employee",
  normalContributionEmployer: "Normal contribution for employer",
  voluntaryContributionEmployee: "Voluntary contribution by employee",
  voluntaryContributionEmployer: "Voluntary contribution by employer",
  otherContribution: "Other Contribution",
  rsapin: "RSA PIN",
  staffId: "Staff ID",
  pfaCode: "PFA code",
  month: "For The Month Of",
  year: "Year Of Contribution",
  total: "TOTAL AMT.(Naira)"
}

export const useScheduleFields = () => {
  const [listOfPfas, setListOfPfas] = useState<Array<PFAListType>>([])
  const codes = listOfPfas.map(pfa => pfa.pfaCode.trim());

  useEffect(() => {
    pfaList(setListOfPfas)
  }, []);

  return {
    fields: [
      {
        label: scheduleHeaders.employerCode,
        key: scheduleHeaders.employerCode,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '012345678901',
        validations: [
          { rule: 'required', errorMessage: 'Employer code is required', level: 'error' },
          { rule: 'regex', errorMessage: 'Employer code must be alphanumeric and up to 12 chars', level: 'error', value: '^[A-Za-z0-9]{1,12}$' }
        ],
      },
      {
        label: scheduleHeaders.month,
        key: scheduleHeaders.month,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: 'January',
        validations: [
          { rule: 'required', errorMessage: 'Month is required', level: 'error' },
          { rule: 'regex', errorMessage: 'Month must be a valid month name', level: 'error', value: '^(?:[Jj][Aa][Nn][Uu][Aa][Rr][Yy]|[Ff][Ee][Bb][Rr][Uu][Aa][Rr][Yy]|[Mm][Aa][Rr][Cc][Hh]|[Aa][Pp][Rr][Ii][Ll]|[Mm][Aa][Yy]|[Jj][Uu][Nn][Ee]|[Jj][Uu][Ll][Yy]|[Aa][Uu][Gg][Uu][Ss][Tt]|[Ss][Ee][Pp][Tt][Ee][Mm][Bb][Ee][Rr]|[Oo][Cc][Tt][Oo][Bb][Ee][Rr]|[Nn][Oo][Vv][Ee][Mm][Bb][Ee][Rr]|[Dd][Ee][Cc][Ee][Mm][Bb][Ee][Rr])$' }
        ],
      },
      {
        label: scheduleHeaders.year,
        key: scheduleHeaders.year,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '2023',
        validations: [
          { rule: 'required', errorMessage: 'Year is required', level: 'error' },
          { rule: 'regex', errorMessage: 'Year must be 4 digits (YYYY)', level: 'error', value: '^\\d{4}$' }
        ],
      },
      {
        label: scheduleHeaders.staffId,
        key: scheduleHeaders.staffId,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: 'SID000001',
        validations: [
          { rule: 'regex', errorMessage: 'Staff ID must be alphanumeric', level: 'error', value: '^[A-Za-z0-9]*$' }
        ],
      },
      {
        label: scheduleHeaders.rsapin,
        key: scheduleHeaders.rsapin,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: 'PEN12345678901',
        validations: [
          { rule: 'required', errorMessage: 'RSA Pin is required', level: 'error' },
          { rule: 'regex', errorMessage: 'RSA Pin must be 15 characters starting with TPEN or PEN', level: 'error', value: '^(TPEN\\d{11}|PEN\\d{12})$' }
        ],
      },
      {
        label: scheduleHeaders.employeeName,
        key: scheduleHeaders.employeeName,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: 'Surname, First Name & Other Name',
        validations: [
          { rule: 'required', errorMessage: 'Employee name is required', level: 'error' },
          // { rule: 'regex', errorMessage: 'Employee name may only contain letters, spaces, hyphens and apostrophes and max 80 chars', level: 'error', value: "^[A-Za-z\s\-']{1,80}$" }
        ],
      },
      {
        label: scheduleHeaders.normalContributionEmployee,
        key: scheduleHeaders.normalContributionEmployee,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '1,000.00',
        validations: [
          { rule: 'required', errorMessage: 'Normal contribution (employee) is required', level: 'error' },
          { rule: 'regex', errorMessage: 'Normal contribution must be numeric with up to 2 decimals and non-negative', level: 'error', value: '^(?!-)(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d{1,2})?$' }
        ],
      },
      {
        label: scheduleHeaders.normalContributionEmployer,
        key: scheduleHeaders.normalContributionEmployer,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '1,000.00',
        validations: [
          { rule: 'required', errorMessage: 'Normal contribution (employer) is required', level: 'error' },
          { rule: 'regex', errorMessage: 'Normal contribution (employer) must be numeric with up to 2 decimals and non-negative', level: 'error', value: '^(?!-)(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d+)?$' }
        ],
      },
      {
        label: scheduleHeaders.voluntaryContributionEmployee,
        key: scheduleHeaders.voluntaryContributionEmployee,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '0.00',
        validations: [
          { rule: 'regex', errorMessage: 'Voluntary contribution (employee) must be numeric with up to 2 decimals or empty', level: 'error', value: '^(?:\\d{1,3}(?:,\\d{3})*(?:\\.\\d{1,2})?|\\d+(?:\\.\\d{1,2})?)?$' }
        ],
      },
      {
        label: scheduleHeaders.voluntaryContributionEmployer,
        key: scheduleHeaders.voluntaryContributionEmployer,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '0.00',
        validations: [
          {
            rule: 'regex',
            errorMessage: 'Voluntary contribution (employer) must be numeric with up to 2 decimals or empty',
            level: 'error',
            value: '^(?:\\d{1,3}(?:,\\d{3})*(?:\\.\\d{1,2})?|\\d+(?:\\.\\d{1,2})?)?$'
          }
        ],
      },
      {
        label: scheduleHeaders.otherContribution,
        key: scheduleHeaders.otherContribution,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '0.00',
        validations: [
          {
            // Allow empty OR a non-negative number with optional commas and decimals
            rule: 'regex',
            errorMessage: 'Other contribution must be a non-negative number or empty',
            level: 'error',
            value: '^(?:\\d{1,3}(?:,\\d{3})*(?:\\.\\d+)?|\\d+(?:\\.\\d+)?)?$'
          },
        ],
      },
      {
        label: scheduleHeaders.total,
        key: scheduleHeaders.total,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '2,000.00',
        validations: [
          { rule: 'required', errorMessage: 'Total amount is required', level: 'error' },
          { rule: 'regex', errorMessage: 'Total amount must be numeric with up to 2 decimals and non-negative', level: 'error', value: '^(?!-)(?:\\d{1,3}(?:,\\d{3})*|\\d+)(?:\\.\\d{1,2})?$' }
        ],
      },
      {
        label: scheduleHeaders.pfaCode,
        key: scheduleHeaders.pfaCode,
        alternateMatches: [],
        fieldType: { type: 'input' },
        example: '021',
        validations: [
          { rule: 'required', errorMessage: 'PFA code is required', level: 'error' },
          { rule: 'regex', errorMessage: 'This field must be a valid PFA code', level: 'error', value: RegExp(`^(${codes.join("|")})$`) }
        ],
      },
    ] as const
  }
}